/* eslint-disable no-underscore-dangle */
import * as React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import axios from 'axios';

import { createReactRenderer } from 'helpers/react-renderer';
import { sortCaseInsensitive } from 'pages/utils/sort';

import Autocomplete from '..';
import GuiDefHiddenInput from '../GuiDefHiddenInput';
import { customFilter, getIsViewPage, useCallbackState } from '../utils';
import { runOnChangeCallbackFuncAsString } from '../Vendors/utils';

const ALL = { value: -1, label: 'All Brands', translate: true };
const NONE = { value: 0, label: 'None', translate: true };

const BrandsAutocomplete: React.FC<AutocompleteProps> = ({
  hasAll = false,
  hasNone = false,
  preventOptionsLoadBeforeFocus = false,
  defaultValue,
  defaultOptions = [],
  placeholder = 'Select or Enter a Brand',
  hiddenInput,
  onChange,
  ...rest
}) => {
  const [value, setValue] = useCallbackState(defaultValue);
  const [options, setOptions] = React.useState(defaultOptions);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInitialFocus, setIsInitialFocus] = React.useState(preventOptionsLoadBeforeFocus);
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    const { errorMsg } = rest;
    if (defaultValue === null && errorMsg) {
      window.alertUser(errorMsg);
    }

    return () => window.merchantos.notifier.cancelWarningTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!defaultValue) {
      if (hasAll) {
        setValue(ALL);
      } else if (hasNone) {
        setValue(NONE);
      }
    } else if (parseInt(defaultValue?.value, 10) === -1) {
      setValue(ALL);
    } else if (parseInt(defaultValue?.value, 10) === 0) {
      setValue(NONE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the Autocomplete is initially focused, it will try to load its options from
  // a hidden container on the page. This functionality is exclusively for the Quick Edit
  // page to improve the performances as the component can be repeated 101 times
  React.useEffect(() => {
    if (isInitialFocus || options.length) return;
    if (document.getElementById('listing_all_brands')) {
      try {
        setOptions(
          JSON.parse(document.getElementById('listing_all_brands')?.getAttribute('value') as string)
        );
      } catch (err) {
        // Silence
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialFocus]);

  const handleOnCreateOption = async (label: string) => {
    setIsLoading(true);
    setValue({ label });

    try {
      const response = await axios.post(
        `/API/Account/${window.merchantos.session.getSystemCustomerID()}/Manufacturer.json`,
        {
          name: label,
        }
      );
      const { data: { Manufacturer: { manufacturerID = null } = {} } = {} } = response;
      const newValue: AutocompleteOption = { value: manufacturerID, label };

      const sortedOptions = [newValue]
        .concat(options)
        .sort((a, b) => sortCaseInsensitive(a.label, b.label));

      setOptions(sortedOptions);
      setValue(newValue);

      if (getIsViewPage()) {
        window.activateViewOnChangeButtons('view');
      }
    } catch (err) {
      const message =
        err.response?.data?.errors?.[0]?.detail ||
        err.response?.data?.message ||
        'There was an error while adding the brand';
      window.alertUser(message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const { value: hiddenInputValue = '' } = value || {};

  return (
    <>
      <Autocomplete
        aria-label="brands-autocomplete-input"
        {...rest}
        value={value}
        options={(
          [
            hasAll ? { ...ALL, translate: true } : undefined,
            hasNone ? { ...NONE, translate: true } : undefined,
          ] as AutocompleteOption[]
        )
          .filter(Boolean)
          .concat(options)}
        filterOption={(
          option: AutocompleteOption & { data: { __isNew__: boolean } },
          inputValue: string
        ) => {
          if (option.data.__isNew__) return true;
          return customFilter(option, inputValue);
        }}
        onFocus={() => {
          setIsInitialFocus(false);
        }}
        placeholder={!rest.isDisabled && placeholder}
        isLoading={isLoading}
        onChange={(newValue: AutocompleteOption) => {
          setValue(newValue, () => {
            if (typeof onChange === 'string') {
              runOnChangeCallbackFuncAsString(onChange);
            }
          });
        }}
        onCreateOption={handleOnCreateOption}
        hasError={isError}
        {...(isError ? { onInputChange: () => setIsError(false) } : null)}
      />

      {hiddenInput ? <GuiDefHiddenInput {...hiddenInput} defaultValue={hiddenInputValue} /> : null}
    </>
  );
};

export default BrandsAutocomplete;

const RenderedComponent = createReactRenderer({
  component: BrandsAutocomplete,
  selector: '[data-react-root="BrandsAutocomplete"]',
});

history.onChangeState(
  [
    {
      name: [
        'item.views.item',
        'item.views.register_item',
        'item.views.quick_add',
        'item.views.register_workorder_item',
        'price_rule.views.price_rule',
        'purchase.views.purchase',
        'item_group.views.item_group',
        'transfer.views.transfer',
        'item.listings.quick_edit_items',
      ],
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);
